import { createAsyncThunk } from '@reduxjs/toolkit';
import { atsJobCreateActions } from './atsJobCreateReducer';

// todo: need to update these, for now added some sample
const actionsConfig = {
  getCurrentJob: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId',
    thunkName: 'currentJob',
    reducerName: 'currentJob',
    responseKey: 'data',
  },
  getOffices: {
    method: 'get',
    endpoint: '/ats_offices',
    thunkName: 'getOffices',
    reducerName: 'getOffices',
    responseKey: 'data',
  },
  getHiringTeam: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId/ats_hiring_teams',
    thunkName: 'hiringTeam',
    reducerName: 'hiringTeam',
    responseKey: 'data',
  },
  updateHiringTeam: {
    method: 'put',
    endpoint: 'ats_jobs/:jobId/ats_hiring_teams',
    thunkName: 'updatedHiringTeam',
    reducerName: 'updatedHiringTeam',
    // responseKey: 'data',
  },
  getJobPost: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId/ats_job_post',
    thunkName: 'getJobPost',
    reducerName: 'getJobPost',
    responseKey: 'data',
  },
  getDefaultJobPost: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId/ats_job_post/new',
    thunkName: 'getDefaultJobPost',
    reducerName: 'defaultJobPost',
    responseKey: 'data',
  },
  // Same endpoint as getDefaultJobPost, but parked in its own slice. The job
  // post `show` response stops returning `ats_form_templates` once a job has
  // had a distinct employee form, which leaves the template pickers with no
  // options to pick from. `new` always returns the list. It needs a separate
  // reducer key because `defaultJobPost` outranks the saved job post in
  // formOptions / resolveEmployeeFormTemplate — writing there would swap the
  // rendered form over to source-template data. See ENG-3654.
  getFormTemplateOptions: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId/ats_job_post/new',
    thunkName: 'getFormTemplateOptions',
    reducerName: 'formTemplateOptions',
    responseKey: 'data',
  },
  createJobPost: {
    method: 'post',
    endpoint: '/ats_jobs/:jobId/ats_job_post',
    thunkName: 'createJobPost',
    reducerName: 'createJobPost',
  },
  updateJobPost: {
    method: 'put',
    endpoint: '/ats_jobs/:jobId/ats_job_post',
    thunkName: 'createJobPost',
    reducerName: 'createJobPost',
  },
  deleteJobPostField: {
    method: 'patch',
    endpoint: '/ats_jobs/:jobId/ats_job_post',
    thunkName: 'updateJobPost',
    reducerName: 'updateJobPost',
  },
  getInterviewPlan: {
    method: 'get',
    endpoint: '/ats_jobs/:jobId/ats_job_stages',
    thunkName: 'getInterviewPlan',
    reducerName: 'getInterviewPlan',
    responseKey: 'data',
  },
  getInterviewPools: {
    method: 'get',
    endpoint: '/user_groups?type=ats_interviewer_pool',
    thunkName: 'getInterviewPools',
    reducerName: 'getInterviewPools',
    responseKey: 'data',
  },
  getHackerrankStatus: {
    method: 'get',
    endpoint: '/hacker_rank/list_interview_templates',
    thunkName: 'getHackerrankStatus',
    reducerName: 'getHackerrankStatus',
    responseKey: 'data',
  },
};

function replacePathParameters(endpoint, pathParams) {
  if (!pathParams || typeof pathParams !== 'object') {
    // Handle the case where pathParams is undefined, null, or not an object
    return endpoint;
  }

  const regex = new RegExp(`:${Object.keys(pathParams).join('|')}`, 'g'); // Create a global regex
  return endpoint.replace(regex, (match) => pathParams[match.slice(1)]); // Use a callback for replacement
}

const apiAction = async (currentAction, config, args) => {
  // redux-thunk hands over (dispatch, getState, extraArgument); only the first
  // and last are used here.
  const [dispatch, _getState, extraArgument] = args;
  const { atsHttp } = extraArgument;
  const { endpoint, method, reducerName: key, responseKey } = currentAction;
  const { requestConfig = {}, isBackground = false, pathParams = {} } = config;
  const { setLoadingState, setApiData } = atsJobCreateActions;

  // If isBackground skip setting loading state to avoid showing loaders
  if (!isBackground) {
    dispatch(setLoadingState({ key, isLoading: true }));
  }
  const updatedEndpoint = replacePathParameters(endpoint, pathParams);
  try {
    const result = await atsHttp[method](updatedEndpoint, { ...requestConfig });
    // ats-https resolves 4xx bodies instead of rejecting (so mutations can read field
    // errors), but that means an error body can land in `.data` for read actions — e.g.
    // a 403's own `data: {}` used to crash JobPipeline's `interviewPlanData.filter`.
    if (responseKey && result?.status >= 400) {
      // Carry status onto the error so the catch block can pass it to
      // consumers (e.g. AtsJobWrapper's 403 → "no access" screen).
      throw Object.assign(
        new Error(
          result?.errors?.join?.(', ') ||
            `Request to ${endpoint} failed with status ${result.status}`
        ),
        { status: result.status }
      );
    }
    const response = responseKey ? result[responseKey] : result;
    dispatch(
      setApiData({
        key,
        data: response,
        status: result?.status,
        isLoading: false,
      })
    );
    return response;
  } catch (error) {
    dispatch(
      setApiData({
        key,
        error: error.message,
        status: error.status,
        isLoading: false,
      })
    );
    console.error(`Error fetching data from ${endpoint}:`, error);
    throw new Error(`Failed to fetch data from ${endpoint}`);
  }
};

export const atsJobCreateApiActions = (actionId, config = {}) => {
  // note: to use params, pass it directly inside requestConfig like below
  // requestConfig: { params: queryParams }
  const currentAction = actionsConfig[actionId];
  const { isApi } = config;
  if (!currentAction)
    return console.error('no actions config found for ats job create');

  const { endpoint, thunkName } = currentAction;

  if (isApi) {
    return (...args) => {
      return apiAction({ ...currentAction, endpoint }, config, args);
    };
  }

  return (...args) => {
    return createAsyncThunk(
      thunkName,
      apiAction({ ...currentAction, endpoint }, config, args)
    );
  };
};
